import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { XIcon, CheckCircleIcon, ChevronUpIcon, ChevronDownIcon, UploadIcon, AlertTriangleIcon, EyeIcon, Trash2Icon } from 'lucide-react';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { Dispute, DisputeStatus, DisputeStage, DisputeReason } from '../../../shared/types/Dispute';
import { getEvidenceRecommendation } from '../utils/evidenceRecommendations';
import { generateDisputes } from '../utils/disputes';
interface UploadedFile {
  id: string;
  name: string;
  size: number;
  uploadedAt: Date;
}
export function ChallengeDispute() {
  const {
    t
  } = useTranslation('disputes');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [challengeStep, setChallengeStep] = useState<'reason' | 'evidence' | 'order' | 'customer' | 'other' | 'review' | 'submitted'>('reason');
  const [selectedChallengeReason, setSelectedChallengeReason] = useState('purchasing_history');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [noEvidenceChecked, setNoEvidenceChecked] = useState(false);
  const [noOrderEvidenceChecked, setNoOrderEvidenceChecked] = useState(false);
  const [noCustomerEvidenceChecked, setNoCustomerEvidenceChecked] = useState(false);
  const [noOtherEvidenceChecked, setNoOtherEvidenceChecked] = useState(false);
  const [showNoEvidenceWarning, setShowNoEvidenceWarning] = useState(false);
  const [reviewConfirmChecked, setReviewConfirmChecked] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    challengeDetails: true,
    transactionDetails: true,
    otherEvidence: true
  });
  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [orderUploadedFiles, setOrderUploadedFiles] = useState<UploadedFile[]>([]);
  const [customerUploadedFiles, setCustomerUploadedFiles] = useState<UploadedFile[]>([]);
  const [otherUploadedFiles, setOtherUploadedFiles] = useState<UploadedFile[]>([]);
  // Use centralized dispute generation
  const allDisputes = useMemo(() => generateDisputes(), []);
  const dispute = useMemo(() => {
    console.log('Looking for dispute ID:', id);
    console.log('Total disputes generated:', allDisputes.length);
    console.log('First 10 dispute IDs:', allDisputes.slice(0, 10).map(d => d.id));
    const found = allDisputes.find(d => d.id === id);
    if (!found) {
      console.log('Dispute not found, using fallback (dispute 1)');
      return allDisputes[0];
    }
    console.log('Found dispute:', found.id, 'Card Brand:', found.cardBrand);
    return found;
  }, [id, allDisputes]);
  // Get evidence recommendations based on card brand and reason code
  const evidenceRecs = useMemo(() => {
    console.group('🔍 ChallengeDispute - Evidence Recommendation Lookup');
    console.log('Dispute ID:', dispute.id);
    console.log('Card Brand:', dispute.cardBrand);
    console.log('Network Reason Code:', dispute.networkReasonCode);
    const result = getEvidenceRecommendation(dispute.cardBrand, dispute.networkReasonCode);
    console.log('Lookup Result:', {
      title: result.title,
      description: result.description,
      recommendationsCount: result.recommendations.length,
      recommendations: result.recommendations.map(r => r.title)
    });
    console.groupEnd();
    return result;
  }, [dispute.cardBrand, dispute.networkReasonCode]);
  const getFileFormatRequirements = () => {
    const cardBrand = dispute.cardBrand.toLowerCase();
    // Debug log to verify card brand
    console.log('Dispute ID:', dispute.id, 'Card Brand:', dispute.cardBrand, 'Lowercase:', cardBrand);
    // Check for Amex
    if (cardBrand === 'amex') {
      return {
        formats: 'Supported formats: TIFF, PDF, JPEG, JPG, PNG, DOC, Maximum individual file size: 4MB, Maximum PDF pages: less than 20 pages, Maximum image size: Not more than 300 x 300 DPI',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, DOC, PNG and maximum combined file sizes: 25MB across all files.'
      };
    }
    // Check for Mastercard
    if (cardBrand === 'mastercard') {
      return {
        formats: 'Accepted file formats: TIFF, PDF, JPEG, PNG. File name restrictions: Use both English characters and numbers. Maximum document / image size: Not more than 300 x 300 DPI and pixel count should be less than 30 million',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, PNG and maximum combined file sizes: 14.5MB across all files.'
      };
    }
    // Visa and other card brands (default)
    return {
      formats: 'Supported formats: TIFF, PDF, PNG, JPEG. Maximum individual file size: 2MB. Maximum page size for TIFF and JPEG: 8.64 inches x 14 inches',
      language: 'Evidence language: English',
      files: 'Supported files: TIFF, TIF, PDF, PNG, JPEG, JPG and maximum combined file sizes: 10MB across all files.'
    };
  };
  const shippingAddress = 'Maybank Tower, 100 Jalan Tun Perak, 50050 Kuala Lumpur, Malaysia';
  const billingAddress = 'Maybank Tower, 100 Jalan Tun Perak, 50050 Kuala Lumpur, Malaysia';
  const formatCurrency = (amount: number, currency: string) => {
    return formatCurrencyAmount(amount, {
      currency,
      fractionDigits: 2
    });
  };
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };
  const simulateFileUpload = (step: 'evidence' | 'order' | 'customer' | 'other') => {
    const mockFile: UploadedFile = {
      id: Math.random().toString(36).substr(2, 9),
      name: 'OHMM-Cairo-Specs-She...9Jul25.pdf',
      size: 1040000,
      uploadedAt: new Date()
    };
    switch (step) {
      case 'evidence':
        setUploadedFiles([...uploadedFiles, mockFile]);
        break;
      case 'order':
        setOrderUploadedFiles([...orderUploadedFiles, mockFile]);
        break;
      case 'customer':
        setCustomerUploadedFiles([...customerUploadedFiles, mockFile]);
        break;
      case 'other':
        setOtherUploadedFiles([...otherUploadedFiles, mockFile]);
        break;
    }
  };
  const removeFile = (fileId: string, step: 'evidence' | 'order' | 'customer' | 'other') => {
    switch (step) {
      case 'evidence':
        setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'order':
        setOrderUploadedFiles(orderUploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'customer':
        setCustomerUploadedFiles(customerUploadedFiles.filter(f => f.id !== fileId));
        break;
      case 'other':
        setOtherUploadedFiles(otherUploadedFiles.filter(f => f.id !== fileId));
        break;
    }
  };
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChallengeReasonNext = () => {
    setChallengeStep('evidence');
  };
  const handleEvidenceNext = () => {
    setChallengeStep('order');
  };
  const handleOrderNext = () => {
    setChallengeStep('customer');
  };
  const handleCustomerNext = () => {
    setChallengeStep('other');
  };
  const handleOtherNext = () => {
    if (noEvidenceChecked && noOrderEvidenceChecked && noCustomerEvidenceChecked && noOtherEvidenceChecked) {
      setShowNoEvidenceWarning(true);
    } else {
      setChallengeStep('review');
    }
  };
  const handleReviewBack = () => {
    setChallengeStep('other');
  };
  const handleSubmit = () => {
    setChallengeStep('submitted');
  };
  const handleClose = () => {
    navigate(`/disputes/${id}`);
  };
  const handleViewAllDisputes = () => {
    navigate('/disputes');
  };
  const handleBackToDisputeDetails = () => {
    navigate(`/disputes/${id}`, {
      state: {
        challenged: true
      }
    });
  };
  return <div className="min-h-screen bg-white" data-id="element-4832">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10" data-id="element-4833">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4834">
          <div className="flex items-center justify-between h-16" data-id="element-4835">
            <h1 className="text-lg font-semibold text-gray-900" data-id="element-4836">
              {t('pages.ChallengeDispute.challenge_dispute')}
            </h1>
            <button onClick={handleClose} aria-label="Close" className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100" data-id="element-4837">
              <XIcon size={20} aria-hidden="true" data-id="element-4838" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-4839">
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-4840">
          {/* Sidebar Navigation */}
          <div className="w-full lg:w-64 flex-shrink-0" data-id="element-4841">
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2 sticky top-24" data-id="element-4842">
              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 text-gray-500" onClick={() => toggleSection('challengeDetails')} data-id="element-4843">
                <span data-id="element-4844">{t('pages.ChallengeDispute.challenge_details')}</span>
                {expandedSections.challengeDetails ? <ChevronUpIcon size={16} className="ml-auto" data-id="element-4845" /> : <ChevronDownIcon size={16} className="ml-auto" data-id="element-4846" />}
              </button>
              {expandedSections.challengeDetails && <div className="ml-3 space-y-1" data-id="element-4847">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'reason' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`} data-id="element-4848">
                    {['evidence', 'order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4849" />}
                    {t('pages.ChallengeDispute.challenge_reason')}
                  </button>
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'evidence' ? 'text-blue-600 bg-blue-50' : challengeStep === 'reason' ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-4850">
                    {['order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4851" />}
                    {t('pages.ChallengeDispute.supporting_evidence')}
                  </button>
                </div>}

              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('transactionDetails')} data-id="element-4852">
                <span data-id="element-4853">{t('pages.ChallengeDispute.transaction_details')}</span>
                {expandedSections.transactionDetails ? <ChevronUpIcon size={16} data-id="element-4854" /> : <ChevronDownIcon size={16} data-id="element-4855" />}
              </button>
              {expandedSections.transactionDetails && <div className="ml-3 space-y-1" data-id="element-4856">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'order' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-4857">
                    {['customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4858" />}
                    {t('pages.ChallengeDispute.order_details')}
                  </button>
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'customer' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-4859">
                    {['other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4860" />}
                    {t('pages.ChallengeDispute.customer_details')}
                  </button>
                </div>}

              <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('otherEvidence')} data-id="element-4861">
                <span data-id="element-4862">{t('pages.ChallengeDispute.other_evidence')}</span>
                {expandedSections.otherEvidence ? <ChevronUpIcon size={16} data-id="element-4863" /> : <ChevronDownIcon size={16} data-id="element-4864" />}
              </button>
              {expandedSections.otherEvidence && <div className="ml-3 space-y-1" data-id="element-4865">
                  <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'other' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order', 'customer'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-4866">
                    {challengeStep === 'review' && <CheckCircleIcon size={16} className="text-green-600" data-id="element-4867" />}
                    {t('pages.ChallengeDispute.other_supporting_documents')}
                  </button>
                </div>}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1" data-id="element-4868">
            {challengeStep === 'reason' && <div className="max-w-3xl" data-id="element-4869">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-4870">
                  <div data-id="element-4871">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4872">
                      {t('pages.ChallengeDispute.challenge_reason')}
                    </h1>
                    <p className="text-gray-600" data-id="element-4873">
                      {t('pages.ChallengeDispute.your_responses_will_help_us_recommend')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-4874">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4875">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-4876">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-4877">
                      <div className="text-gray-500" data-id="element-4878">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-4879">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-4880">
                      <div className="text-gray-500" data-id="element-4881">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-4882">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-4883">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-4884">
                  <div data-id="element-4885">
                    <label className="block text-base font-semibold text-gray-900 mb-1" data-id="element-4886">
                      {t('pages.ChallengeDispute.why_are_you_challenging_the_dispute')}
                    </label>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-4887">
                      {t('pages.ChallengeDispute.choose_the_closest_option')}
                    </p>
                    <div className="space-y-3" data-id="element-4888">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4889">
                        <input type="radio" name="challengeReason" value="withdrew" checked={selectedChallengeReason === 'withdrew'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4890" />

                        <span className="text-base text-gray-900" data-id="element-4891">
                          {t('pages.ChallengeDispute.the_customer_withdrew_the_dispute')}
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4892">
                        <input type="radio" name="challengeReason" value="refunded" checked={selectedChallengeReason === 'refunded'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4893" />

                        <span className="text-base text-gray-900" data-id="element-4894">
                          {t('pages.ChallengeDispute.the_customer_has_already_been_refunded')}
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4895">
                        <input type="radio" name="challengeReason" value="purchasing_history" checked={selectedChallengeReason === 'purchasing_history'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4896" />

                        <span className="text-base text-gray-900" data-id="element-4897">
                          {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                        </span>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4898">
                        <input type="radio" name="challengeReason" value="other" checked={selectedChallengeReason === 'other'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4899" />

                        <span className="text-base text-gray-900" data-id="element-4900">
                          {t('pages.ChallengeDispute.other_reasons')}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div data-id="element-4901">
                    <label className="block text-base font-semibold text-gray-900 mb-1" data-id="element-4902">
                      {t('pages.ChallengeDispute.what_type_of_productservice_was_the')}
                    </label>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-4903">
                      {t('pages.ChallengeDispute.choose_the_closest_option')}
                    </p>
                    <div className="space-y-3" data-id="element-4904">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4905">
                        <input type="radio" name="productType" value="physical" checked={selectedProductType === 'physical'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4906" />

                        <div className="flex-1" data-id="element-4907">
                          <div className="text-base text-gray-900 font-medium" data-id="element-4908">
                            {t('pages.ChallengeDispute.physical_product')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-4909">
                            {t('pages.ChallengeDispute.physical_goods_to_be_delivered_to')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4910">
                        <input type="radio" name="productType" value="digital" checked={selectedProductType === 'digital'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4911" />

                        <div className="flex-1" data-id="element-4912">
                          <div className="text-base text-gray-900 font-medium" data-id="element-4913">
                            {t('pages.ChallengeDispute.digital_product_or_service')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-4914">
                            {t('pages.ChallengeDispute.purchased_access_to_software_digital_files')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4915">
                        <input type="radio" name="productType" value="offline" checked={selectedProductType === 'offline'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4916" />

                        <div className="flex-1" data-id="element-4917">
                          <div className="text-base text-gray-900 font-medium" data-id="element-4918">
                            {t('pages.ChallengeDispute.offline_service')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-4919">
                            {t('pages.ChallengeDispute.labour_or_assistant_services_provided_either')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4920">
                        <input type="radio" name="productType" value="reservation" checked={selectedProductType === 'reservation'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4921" />

                        <div className="flex-1" data-id="element-4922">
                          <div className="text-base text-gray-900 font-medium" data-id="element-4923">
                            {t('pages.ChallengeDispute.reservation_or_booking')}
                          </div>
                          <div className="text-sm text-gray-500 mt-0.5" data-id="element-4924">
                            {t('pages.ChallengeDispute.services_related_to_reservations_or_bookings')}
                          </div>
                        </div>
                      </label>
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-4925">
                        <input type="radio" name="productType" value="other_product" checked={selectedProductType === 'other_product'} onChange={e => setSelectedProductType(e.target.value)} className="mt-0.5 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-4926" />

                        <span className="text-base text-gray-900" data-id="element-4927">
                          {t('pages.ChallengeDispute.other')}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-4928">
                  <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-4929">
                    {t('pages.ChallengeDispute.cancel')}
                  </button>
                  <button onClick={handleChallengeReasonNext} disabled={!selectedProductType} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-300" data-id="element-4930">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'evidence' && <div className="max-w-3xl" data-id="element-4931">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-4932">
                  <div data-id="element-4933">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4934">
                      {evidenceRecs.title}
                    </h1>
                    <p className="text-gray-600" data-id="element-4935">
                      {evidenceRecs.description}
                      {evidenceRecs.learnMoreLink && <>
                          {' '}
                          <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-4936">
                            {evidenceRecs.learnMoreLink}
                          </a>
                        </>}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-4937">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4938">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-4939">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-4940">
                      <div className="text-gray-500" data-id="element-4941">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-4942">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-4943">
                      <div className="text-gray-500" data-id="element-4944">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-4945">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-4946">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-4947">
                  <div data-id="element-4948">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-4949">
                      {t('pages.ChallengeDispute.recommended_evidence')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-4950">
                      {t('pages.ChallengeDispute.based_on_your_challenge_reason_we')}
                    </p>
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 mb-4" data-id="element-4951">
                      {evidenceRecs.recommendations.map((rec, index) => <li key={index} data-id="element-4952">
                          <strong data-id="element-4953">{rec.title}</strong>
                          {rec.description && `: ${rec.description}`}
                        </li>)}
                    </ul>
                  </div>

                  <div data-id="element-4954">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-4955">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-4956">
                      {(() => {
                    const requirements = getFileFormatRequirements();
                    return <>
                            {requirements.formats}
                            <br data-id="element-4957" />
                            {requirements.language}
                            <br data-id="element-4958" />
                            {requirements.files}
                          </>;
                  })()}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {uploadedFiles.length > 0 && <div className="space-y-3" data-id="element-4959">
                      {uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-4960">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-4961">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4962">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4963" />

                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4964" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-4965">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-4966">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-4967">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-4968">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-4969">
                              <EyeIcon size={20} data-id="element-4970" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'evidence')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-4971">
                              <Trash2Icon size={20} data-id="element-4972" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-4973">
                    <button disabled={noEvidenceChecked} onClick={() => simulateFileUpload('evidence')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-4974">
                      <UploadIcon size={16} data-id="element-4975" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-4976">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-4977" />
                  </div>

                  <div data-id="element-4978">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-4979">
                      <input type="checkbox" checked={noEvidenceChecked} onChange={e => setNoEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-4980" />

                      <span className="text-sm text-gray-900" data-id="element-4981">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-4982">
                  <button onClick={() => setChallengeStep('reason')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-4983">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleEvidenceNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-4984">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'order' && <div className="max-w-3xl" data-id="element-4985">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-4986">
                  <div data-id="element-4987">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4988">
                      {t('pages.ChallengeDispute.order_details')}
                    </h1>
                    <p className="text-gray-600" data-id="element-4989">
                      {t('pages.ChallengeDispute.please_provide_as_much_information_as')}
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-4990">
                      {t('pages.ChallengeDispute.the_details_below_have_been_prefilled')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-4991">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-4992">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-4993">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-4994">
                      <div className="text-gray-500" data-id="element-4995">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-4996">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-4997">
                      <div className="text-gray-500" data-id="element-4998">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-4999">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5000">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5001">
                  <div data-id="element-5002">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5003">
                      {t('pages.ChallengeDispute.product_details')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5004">
                      <span data-id="element-5005">
                        {t('pages.ChallengeDispute.iphone7_5')}{' '}
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5006">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5007">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5008" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5009">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5010">
                      {t('pages.ChallengeDispute.shipping_address')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5011">
                      <span data-id="element-5012">{shippingAddress}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5013">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5014">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5015" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5016">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5017">
                      {t('pages.ChallengeDispute.shipping_carrier')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5018">
                      <span data-id="element-5019">{t('pages.ChallengeDispute.sameday')}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5020">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5021">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5022" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5023">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5024">
                      {t('pages.ChallengeDispute.provide_additional_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5025">
                      {t('pages.ChallengeDispute.as_the_payment_was_for_a')}{' '}
                      <span className="font-medium" data-id="element-5026">
                        {t('pages.ChallengeDispute.physical_product')}
                      </span>
                      {t('pages.ChallengeDispute.we_recommend_you_to_also_provide')}
                    </p>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-4" data-id="element-5027">
                      <li data-id="element-5028">{t('pages.ChallengeDispute.shipping_date')}</li>
                      <li data-id="element-5029">
                        {t('pages.ChallengeDispute.shipping_tracking_number')}
                      </li>
                    </ul>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5030">
                      {t('pages.ChallengeDispute.you_can_upload_any_document_containing')}
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5031">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-5032" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-5033" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  <div data-id="element-5034">
                    <button disabled={noOrderEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOrderEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5035">
                      <UploadIcon size={16} data-id="element-5036" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-5037">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noOrderEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOrderEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5038" />
                  </div>

                  <div data-id="element-5039">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5040">
                      <input type="checkbox" checked={noOrderEvidenceChecked} onChange={e => setNoOrderEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5041" />

                      <span className="text-sm text-gray-900" data-id="element-5042">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5043">
                  <button onClick={() => setChallengeStep('evidence')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5044">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleOrderNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5045">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'customer' && <div className="max-w-3xl" data-id="element-5046">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5047">
                  <div data-id="element-5048">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5049">
                      {t('pages.ChallengeDispute.customer_details')}
                    </h1>
                    <p className="text-gray-600" data-id="element-5050">
                      {t('pages.ChallengeDispute.please_provide_as_much_information_as_2')}
                    </p>
                    <p className="text-gray-600 mt-2" data-id="element-5051">
                      {t('pages.ChallengeDispute.the_details_below_have_been_prefilled')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5052">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5053">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5054">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5055">
                      <div className="text-gray-500" data-id="element-5056">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5057">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5058">
                      <div className="text-gray-500" data-id="element-5059">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5060">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5061">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5062">
                  <div data-id="element-5063">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5064">
                      {t('pages.ChallengeDispute.customer_name')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5065">
                      <span data-id="element-5066">{t('pages.ChallengeDispute.test_name')}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5067">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5068">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5069" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5070">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5071">
                      {t('pages.ChallengeDispute.customer_email')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5072">
                      <span data-id="element-5073">
                        {t('pages.ChallengeDispute.customerexamplecom')}
                      </span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5074">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5075">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5076" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5077">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5078">
                      {t('pages.ChallengeDispute.customer_purchase_ip')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5079">
                      <span data-id="element-5080">35.221.170.121</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5081">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5082">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5083" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5084">
                    <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-5085">
                      {t('pages.ChallengeDispute.customer_billing_address')}
                    </label>
                    <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5086">
                      <span data-id="element-5087">{billingAddress}</span>
                      <button className="text-blue-600 hover:text-blue-700" data-id="element-5088">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5089">
                          <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-5090" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div data-id="element-5091">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5092">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5093">
                      {t('pages.ChallengeDispute.we_recommend_you_to_provide_the')}
                    </p>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-5094">
                      {t('pages.ChallengeDispute.you_can_upload_any_document_containing_2')}
                    </p>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5095">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-5096" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-5097" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {customerUploadedFiles.length > 0 && <div className="space-y-3" data-id="element-5098">
                      {customerUploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-5099">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-5100">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5101">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5102" />

                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5103" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-5104">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-5105">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-5106">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-5107">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5108">
                              <EyeIcon size={20} data-id="element-5109" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'customer')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5110">
                              <Trash2Icon size={20} data-id="element-5111" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-5112">
                    <button disabled={noCustomerEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noCustomerEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5113">
                      <UploadIcon size={16} data-id="element-5114" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-5115">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noCustomerEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noCustomerEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5116" />
                  </div>

                  <div data-id="element-5117">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5118">
                      <input type="checkbox" checked={noCustomerEvidenceChecked} onChange={e => setNoCustomerEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5119" />

                      <span className="text-sm text-gray-900" data-id="element-5120">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5121">
                  <button onClick={() => setChallengeStep('order')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5122">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleCustomerNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5123">
                    {t('pages.ChallengeDispute.next')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'other' && <div className="max-w-3xl" data-id="element-5124">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5125">
                  <div data-id="element-5126">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5127">
                      {t('pages.ChallengeDispute.other_supporting_documents')}
                    </h1>
                    <p className="text-gray-600" data-id="element-5128">
                      {t('pages.ChallengeDispute.if_you_have_any_other_documents')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5129">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5130">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5131">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5132">
                      <div className="text-gray-500" data-id="element-5133">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5134">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5135">
                      <div className="text-gray-500" data-id="element-5136">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5137">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5138">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5139">
                  <div data-id="element-5140">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5141">
                      {t('pages.ChallengeDispute.provide_supporting_evidence')}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-5142">
                      {getFileFormatRequirements().formats}
                      <br data-id="element-5143" />
                      {getFileFormatRequirements().language}
                      <br data-id="element-5144" />
                      {getFileFormatRequirements().files}
                    </p>
                  </div>

                  {/* Uploaded Files Display */}
                  {otherUploadedFiles.length > 0 && <div className="space-y-3" data-id="element-5145">
                      {otherUploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg" data-id="element-5146">
                          <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded flex items-center justify-center" data-id="element-5147">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5148">
                              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5149" />

                              <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5150" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-5151">
                            <div className="text-sm font-medium text-gray-900 truncate" data-id="element-5152">
                              {file.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-5153">
                              {formatFileSize(file.size)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-5154">
                            <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5155">
                              <EyeIcon size={20} data-id="element-5156" />
                            </button>
                            <button onClick={() => removeFile(file.id, 'other')} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" data-id="element-5157">
                              <Trash2Icon size={20} data-id="element-5158" />
                            </button>
                          </div>
                        </div>)}
                    </div>}

                  <div data-id="element-5159">
                    <button disabled={noOtherEvidenceChecked} onClick={() => simulateFileUpload('other')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOtherEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-5160">
                      <UploadIcon size={16} data-id="element-5161" />
                      {t('pages.ChallengeDispute.upload_file')}
                    </button>
                  </div>

                  <div data-id="element-5162">
                    <textarea placeholder={t('pages.ChallengeDispute.attrs.include_any_additional_file_descriptions_or')} rows={4} disabled={noOtherEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOtherEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-5163" />
                  </div>

                  <div data-id="element-5164">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5165">
                      <input type="checkbox" checked={noOtherEvidenceChecked} onChange={e => setNoOtherEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5166" />

                      <span className="text-sm text-gray-900" data-id="element-5167">
                        {t('pages.ChallengeDispute.i_do_not_have_any_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5168">
                  <button onClick={() => setChallengeStep('customer')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5169">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleOtherNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-5170">
                    {t('pages.ChallengeDispute.review')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'review' && <div className="max-w-3xl" data-id="element-5171">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6" data-id="element-5172">
                  <div data-id="element-5173">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-5174">
                      {t('pages.ChallengeDispute.review_your_challenge')}
                    </h1>
                    <p className="text-gray-600" data-id="element-5175">
                      {t('pages.ChallengeDispute.please_confirm_that_the_information_you')}
                    </p>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0" data-id="element-5176">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-5177">
                      {t('pages.ChallengeDispute.dispute_id')}{' '}
                      {dispute.disputeId}
                    </div>
                    <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-5178">
                      {t('pages.ChallengeDispute.view_details')}
                    </button>
                    <div className="mt-2 text-sm" data-id="element-5179">
                      <div className="text-gray-500" data-id="element-5180">
                        {t('pages.ChallengeDispute.dispute_amount')}
                      </div>
                      <div className="font-medium" data-id="element-5181">
                        {formatCurrency(dispute.amount, dispute.currency)}
                      </div>
                    </div>
                    <div className="mt-2 text-sm" data-id="element-5182">
                      <div className="text-gray-500" data-id="element-5183">
                        {t('pages.ChallengeDispute.response_due')}
                      </div>
                      <div className="font-medium" data-id="element-5184">
                        {formatDateTime(dispute.dueDate)}
                      </div>
                      <div className="text-red-600 font-medium" data-id="element-5185">
                        {t('pages.ChallengeDispute.due_in_1_hour')}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6" data-id="element-5186">
                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-5187">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5188">
                      {t('pages.ChallengeDispute.challenge_details')}
                    </h3>
                    <div className="space-y-3" data-id="element-5189">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5190">
                        <span className="text-sm text-gray-600" data-id="element-5191">
                          {t('pages.ChallengeDispute.reason_for_challenge')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5192">
                          {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5193">
                        <span className="text-sm text-gray-600" data-id="element-5194">
                          {t('pages.ChallengeDispute.type_of_product_or_service')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5195">
                          {t('pages.ChallengeDispute.physical_product')}
                        </span>
                      </div>
                      {uploadedFiles.length > 0 && <div className="flex flex-col gap-2" data-id="element-5196">
                          <span className="text-sm text-gray-600" data-id="element-5197">
                            {t('pages.ChallengeDispute.supporting_evidence')}
                          </span>
                          <div className="space-y-2" data-id="element-5198">
                            {uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-2 text-sm" data-id="element-5199">
                                <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-5200">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5201">
                                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5202" />

                                    <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5203" />
                                  </svg>
                                </div>
                                <div className="flex-1" data-id="element-5204">
                                  <div className="text-gray-900 font-medium" data-id="element-5205">
                                    {file.name}
                                  </div>
                                  <div className="text-gray-500" data-id="element-5206">
                                    {formatFileSize(file.size)}
                                  </div>
                                </div>
                                <button className="text-blue-600 hover:text-blue-700" data-id="element-5207">
                                  <EyeIcon size={16} data-id="element-5208" />
                                </button>
                              </div>)}
                          </div>
                        </div>}
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6" data-id="element-5209">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5210">
                      {t('pages.ChallengeDispute.transaction_details')}
                    </h3>
                    <div className="space-y-3" data-id="element-5211">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5212">
                        <span className="text-sm text-gray-600" data-id="element-5213">
                          {t('pages.ChallengeDispute.product_details')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5214">
                          {t('pages.ChallengeDispute.iphone7_5')}{' '}
                          {formatCurrency(dispute.amount, dispute.currency)}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5215">
                        <span className="text-sm text-gray-600" data-id="element-5216">
                          {t('pages.ChallengeDispute.shipping_address')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5217">
                          {shippingAddress}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5218">
                        <span className="text-sm text-gray-600" data-id="element-5219">
                          {t('pages.ChallengeDispute.customer_name')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5220">
                          {t('pages.ChallengeDispute.test_name')}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5221">
                        <span className="text-sm text-gray-600" data-id="element-5222">
                          {t('pages.ChallengeDispute.customer_email')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5223">
                          {t('pages.ChallengeDispute.customerexamplecom')}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5224">
                        <span className="text-sm text-gray-600" data-id="element-5225">
                          {t('pages.ChallengeDispute.customer_purchase_ip')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5226">
                          35.221.170.121
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-2" data-id="element-5227">
                        <span className="text-sm text-gray-600" data-id="element-5228">
                          {t('pages.ChallengeDispute.customer_billing_address')}
                        </span>
                        <span className="text-sm text-gray-900 sm:text-right" data-id="element-5229">
                          {billingAddress}
                        </span>
                      </div>
                    </div>
                  </div>

                  {(noEvidenceChecked || noOrderEvidenceChecked || noCustomerEvidenceChecked || noOtherEvidenceChecked) && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-5230">
                      <AlertTriangleIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-5231" />

                      <p className="text-sm text-gray-900" data-id="element-5232">
                        {t('pages.ChallengeDispute.please_attach_at_least_one_of')}
                      </p>
                    </div>}

                  <div data-id="element-5233">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-5234">
                      <input type="checkbox" checked={reviewConfirmChecked} onChange={e => setReviewConfirmChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-5235" />

                      <span className="text-sm text-gray-900" data-id="element-5236">
                        {t('pages.ChallengeDispute.i_understand_that_the_current_evidence')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-5237">
                  <button onClick={handleReviewBack} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-5238">
                    {t('pages.ChallengeDispute.back')}
                  </button>
                  <button onClick={handleSubmit} disabled={!reviewConfirmChecked} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5239">
                    {t('pages.ChallengeDispute.submit')}
                  </button>
                </div>
              </div>}

            {challengeStep === 'submitted' && <div className="max-w-3xl mx-auto py-12" data-id="element-5240">
                <div className="text-center mb-8" data-id="element-5241">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-6" data-id="element-5242">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5243">
                      <path d="M24 4L6 14L24 24L42 14L24 4Z" fill="#10B981" stroke="#10B981" strokeWidth="2" strokeLinejoin="round" data-id="element-5244" />

                      <path d="M6 24L24 34L42 24" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5245" />

                      <path d="M6 34L24 44L42 34" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5246" />
                    </svg>
                  </div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-5247">
                    {t('pages.ChallengeDispute.your_dispute_challenge_has_been_submitted')}
                  </h1>
                  <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-5248">
                    {t('pages.ChallengeDispute.thank_you_for_submitting_your_information')}
                  </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-8 space-y-8" data-id="element-5249">
                  <div data-id="element-5250">
                    <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-5251">
                      {t('pages.ChallengeDispute.dispute_challenge')}
                    </h2>

                    <div className="space-y-6" data-id="element-5252">
                      <div data-id="element-5253">
                        <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-5254">
                          {t('pages.ChallengeDispute.challenge_details')}
                        </h3>
                        <div className="space-y-3" data-id="element-5255">
                          <div className="flex" data-id="element-5256">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5257">
                              {t('pages.ChallengeDispute.reason_for_challenge')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5258">
                              {t('pages.ChallengeDispute.the_customer_has_a_purchasing_history')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5259">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5260">
                              {t('pages.ChallengeDispute.type_of_product_or_service')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5261">
                              {t('pages.ChallengeDispute.physical_product')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5262">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5263">
                              {t('pages.ChallengeDispute.supporting_evidence')}
                            </span>
                            <div className="flex-1" data-id="element-5264">
                              {uploadedFiles.length > 0 ? uploadedFiles.map(file => <div key={file.id} className="flex items-center gap-3 mb-2" data-id="element-5265">
                                    <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-5266">
                                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5267">
                                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5268" />

                                        <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5269" />
                                      </svg>
                                    </div>
                                    <div className="flex-1" data-id="element-5270">
                                      <div className="text-sm font-medium text-gray-900" data-id="element-5271">
                                        {file.name}
                                      </div>
                                      <div className="text-sm text-gray-500" data-id="element-5272">
                                        {formatFileSize(file.size)}
                                      </div>
                                    </div>
                                    <button className="text-blue-600 hover:text-blue-700" data-id="element-5273">
                                      <EyeIcon size={18} data-id="element-5274" />
                                    </button>
                                  </div>) : <span className="text-sm text-gray-900" data-id="element-5275">
                                  {t('pages.ChallengeDispute.no_files_uploaded')}
                                </span>}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div data-id="element-5276">
                        <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-5277">
                          {t('pages.ChallengeDispute.transaction_details')}
                        </h3>
                        <div className="space-y-3" data-id="element-5278">
                          <div className="flex" data-id="element-5279">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5280">
                              {t('pages.ChallengeDispute.product_details')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5281">
                              {t('pages.ChallengeDispute.iphone7_5')}{' '}
                              {formatCurrency(dispute.amount, dispute.currency)}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5282">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5283">
                              {t('pages.ChallengeDispute.shipping_address')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5284">
                              {shippingAddress}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5285">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5286">
                              {t('pages.ChallengeDispute.customer_name')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5287">
                              {t('pages.ChallengeDispute.test_name')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5288">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5289">
                              {t('pages.ChallengeDispute.customer_email')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5290">
                              {t('pages.ChallengeDispute.customerexamplecom')}
                            </span>
                          </div>
                          <div className="flex" data-id="element-5291">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5292">
                              {t('pages.ChallengeDispute.customer_purchase_ip')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5293">
                              35.221.170.121
                            </span>
                          </div>
                          <div className="flex" data-id="element-5294">
                            <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-5295">
                              {t('pages.ChallengeDispute.customer_billing_address')}
                            </span>
                            <span className="text-sm text-gray-900" data-id="element-5296">
                              {billingAddress}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3" data-id="element-5297">
                    <button onClick={handleViewAllDisputes} className="px-6 py-3 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 rounded-md hover:bg-blue-50" data-id="element-5298">
                      {t('pages.ChallengeDispute.view_all_disputes')}
                    </button>
                    <button onClick={handleBackToDisputeDetails} className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-5299">
                      {t('pages.ChallengeDispute.back_to_dispute_details')}
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* No Evidence Warning Modal */}
      {showNoEvidenceWarning && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5300">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-5301">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowNoEvidenceWarning(false)} data-id="element-5302">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-5303"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-5304">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-5305">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-5306">
                <div className="flex items-start gap-4" data-id="element-5307">
                  <div className="flex-shrink-0" data-id="element-5308">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-5309">
                      <AlertTriangleIcon size={24} className="text-red-600" data-id="element-5310" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-5311">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-5312">
                      {t('pages.ChallengeDispute.are_you_sure_you_want_to')}
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-5313">
                      {t('pages.ChallengeDispute.submitting_a_challenge_without_supporting_evidence')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3" data-id="element-5314">
                <button onClick={() => setShowNoEvidenceWarning(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 order-2 sm:order-1" data-id="element-5315">
                  {t('pages.ChallengeDispute.go_back_and_add_evidence')}
                </button>
                <button onClick={() => {
              setShowNoEvidenceWarning(false);
              setChallengeStep('review');
            }} className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 order-1 sm:order-2" data-id="element-5316">
                  {t('pages.ChallengeDispute.proceed_anyway')}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}